// src/pages/Menu/Articulacion/Details/EntityDetailsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Building2,
  Link as LinkIcon,
  Mail,
  Phone,
  Briefcase,
  ChevronRight,
  ChevronDown,
  ShieldAlert,
  MoreVertical,
  Plus,
  Edit3,
  Trash2,
  PhoneCall,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import TooltipHelper from "../../../../components/common/TooltipHelper";

import WorkerDetailsModal from "./WorkerDetailsModal";

import {
  getRegionalEntity,
  deleteRegionalEntity,
} from "../../../../services/regionalEntitiesService";
import {
  listRegionalWorkers,
  getRegionalWorker,
  createRegionalWorker,
  updateRegionalWorker,
  deleteRegionalWorker,
} from "../../../../services/regionalWorkersService";

/* ======================== constantes de estilo ======================== */
const COLOR_PRIMARY = "#0488AF"; // (ya no se usa para botones principales, se deja por compat si se requiere)
const COLOR_DANGER = "#C42251";  // Eliminar
const COLOR_CANCEL = "#6b7280";  // Cancelar (solo para algunos diálogos Swal)

// Valor especial para filtrar "SIN SUBDIVISIÓN" (bucket vacío)
const NO_BUCKET_VALUE = "__NO_BUCKET__";

/* ======================== helpers ======================== */
const Label = ({ children, className }) => (
  <label className={`block text-xs text-gray-600 ${className || ""}`}>
    {children}
  </label>
);

const norm = (s) => String(s ?? "").toLowerCase().trim();
const clean = (o) =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined)
  );
const boolish = (v) => v === true || v === "true";

const useDebounced = (val, ms = 250) => {
  const [d, setD] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setD(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return d;
};

// Etiqueta para encabezado de sección
const bucketLabel = (b) => (b ? b : "Contactos");


/* ================= Autocomplete estilizado ================= */
function AutocompleteInput({
  value,
  onChange,
  options = [],
  placeholder,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [hoverIndex, setHoverIndex] = useState(-1);
  const ref = React.useRef(null);

  useEffect(() => setQuery(value || ""), [value]);

  useEffect(() => {
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const items = useMemo(() => {
    const q = String(query).toLowerCase().trim();
    const base = q
      ? options.filter((o) => o.toLowerCase().includes(q))
      : options;
    return Array.from(new Set(base)).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [query, options]);

  const select = (val) => {
    onChange(val);
    setQuery(val);
    setOpen(false);
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIndex((i) => Math.min(i + 1, items.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (items[hoverIndex]) select(items[hoverIndex]);
      else setOpen(false);
    }
  };

  return (
    <div ref={ref} className={`relative ${className || ""}`}>
      <input
        className="w-full rounded-lg border p-2 pr-9 focus:ring-2 focus:ring-[#0488AF] outline-none"
        placeholder={placeholder}
        value={query}
        onChange={onInput}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 hover:bg-gray-100"
        onClick={() => setOpen((v) => !v)}
        aria-label="Abrir opciones"
      >
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border bg-white shadow-lg max-h-56 overflow-auto">
          {items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              Sin sugerencias
            </div>
          ) : (
            items.map((opt, idx) => (
              <button
                key={opt}
                type="button"
                onMouseEnter={() => setHoverIndex(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(opt)}
                className={`w-full text-left px-3 py-2 text-sm ${
                  idx === hoverIndex
                    ? "bg-gray-100"
                    : "hover:bg-gray-50"
                }`}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   MODAL PRINCIPAL
   ========================================================= */
export default function EntityDetailsModal({
  open,
  entityId,
  onClose,
  onChanged,
  onEditEntity,
}) {
  const [entity, setEntity] = useState(null);

  // Filtros/contactos
  const [q, setQ] = useState("");
  const [bucketFilter, setBucketFilter] = useState(""); // "" = todas, NO_BUCKET_VALUE = sin subdivisión
  const [onlyAuthority, setOnlyAuthority] = useState(false);
  const [sortMode, setSortMode] = useState("default"); // orden de contactos
  const [bucketSort, setBucketSort] = useState("default"); // orden de subdivisiones
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modales
  const [contactDlg, setContactDlg] = useState(null); // {values?, editId?, presetBucket?, parentId?}
  const [viewer, setViewer] = useState(null); // objeto worker o null

  const qDeb = useDebounced(q, 250);

  // Interpretación real del filtro de subdivisión
  // "" = todas, NO_BUCKET_VALUE => bucket vacío
  const bucketFilterValue =
    bucketFilter === NO_BUCKET_VALUE ? "" : bucketFilter;

  // Subdivisiones “virtuales” POR ENTIDAD
  const [extraBucketsByEntity, setExtraBucketsByEntity] =
    useState({});
  const extraBuckets = extraBucketsByEntity[entityId] || [];
  const setExtraBuckets = (updater) =>
    setExtraBucketsByEntity((prev) => {
      const current = prev[entityId] || [];
      const next =
        typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [entityId]: next };
    });

  const [menuBucket, setMenuBucket] = useState(null);

  const isVirtualBucket = (name) =>
    extraBuckets.some((b) => norm(b) === norm(name));
  const bucketHasWorkers = (name) =>
    rows.some((w) => norm(w.bucket || "") === norm(name));

  /* ---------- Carga entidad ---------- */
  useEffect(() => {
    if (!open || !entityId) return;
    (async () => {
      try {
        const r = await getRegionalEntity(entityId);
        setEntity(r?.data ?? r ?? null);
      } catch {
        setEntity(null);
      }
    })();
  }, [open, entityId]);

  /* Al cambiar de entidad, cerramos menú */
  useEffect(() => {
    setMenuBucket(null);
  }, [entityId]);

  // Al cerrar el modal, asegurarse de cerrar el menú de opciones
  useEffect(() => {
    if (!open) {
      setMenuBucket(null);
    }
  }, [open]);

  /* ---------- Carga + normaliza + filtro ---------- */
  useEffect(() => {
    if (!open || !entityId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const data = await listRegionalWorkers({
          entityId,
          q: qDeb || undefined,
          bucket: bucketFilterValue || undefined,
          onlyAuthority: onlyAuthority ? "true" : undefined,
        });

        const listRaw = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        const nQ = norm(qDeb);

        const filtered = listRaw
          .map((w) => ({
            ...w,
            isAuthority: boolish(w.isAuthority ?? w.is_authority),
            parentId: w.parentId ?? w.parent_id ?? null,
            bucket: w.bucket ?? "",
            altPhone: w.altPhone ?? w.alt_phone ?? "",
            assistantName: w.assistantName ?? w.assistant_name ?? "",
            assistantPhone: w.assistantPhone ?? w.assistant_phone ?? "",
            assistantEmail: w.assistantEmail ?? w.assistant_email ?? "",
          }))
          .filter((w) => {
            const textOK =
              !nQ ||
              norm(w.name).includes(nQ) ||
              norm(w.role).includes(nQ) ||
              norm(w.email).includes(nQ) ||
              norm(w.phone).includes(nQ) ||
              norm(w.altPhone).includes(nQ);

            const bucketOK =
              !bucketFilterValue ||
              String(w.bucket || "") === bucketFilterValue;
            const bossOK = !onlyAuthority || !!w.isAuthority;
            return textOK && bucketOK && bossOK;
          });

        if (!cancel) setRows(filtered);
      } catch {
        if (!cancel) setRows([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, entityId, qDeb, bucketFilter, bucketFilterValue, onlyAuthority]);

  /* ---------- Cerrar menú 3 puntitos al click afuera o Esc ---------- */
  useEffect(() => {
    if (!menuBucket) return;
    const onDown = (e) => {
      const inside = e.target.closest("[data-bucket-menu-area]");
      if (!inside) setMenuBucket(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuBucket(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuBucket]);

  /* ---------- Buckets + árbol por bucket ---------- */
  const buckets = useMemo(() => {
    const byBucket = rows.reduce((acc, w) => {
      const b = (w.bucket || "").trim();
      (acc[b] ||= []).push(w);
      return acc;
    }, {});

    // incluir subdivisiones “virtuales” aunque estén vacías
    extraBuckets.forEach((b) => {
      if (!(b in byBucket)) byBucket[b] = [];
    });

    const cmpName = (a, b) =>
      String(a.name || "").localeCompare(
        String(b.name || ""),
        undefined,
        {
          sensitivity: "base",
        }
      );

    const buildTree = (items) => {
      const byId = new Map(
        items.map((w) => [w.id, { ...w, children: [] }])
      );
      const roots = [];
      for (const w of items) {
        const node = byId.get(w.id);
        const pid = w.parentId ?? null;
        if (pid && byId.has(pid)) byId.get(pid).children.push(node);
        else roots.push(node);
      }

      const sortFn = (a, b) => {
        if (sortMode === "az") return cmpName(a, b);
        if (sortMode === "za") return cmpName(b, a);
        // default: autoridad primero, luego A→Z
        return (
          (b.isAuthority === true) - (a.isAuthority === true) ||
          cmpName(a, b)
        );
      };

      const walk = (arr) => {
        arr.sort(sortFn);
        arr.forEach(
          (n) => n.children?.length && walk(n.children)
        );
      };
      walk(roots);
      return roots;
    };

    // comparador para ORDENAR SUBDIVISIONES
    const cmpBuckets = (A, B) => {
      const a = A || "";
      const b = B || "";
      const aEmpty = a === "";
      const bEmpty = b === "";

      if (bucketSort === "az") {
        if (aEmpty && !bEmpty) return 1; // vacía al final
        if (!aEmpty && bEmpty) return -1;
        return a.localeCompare(b, undefined, {
          sensitivity: "base",
        });
      }
      if (bucketSort === "za") {
        if (aEmpty && !bEmpty) return -1; // vacía al inicio
        if (!aEmpty && bEmpty) return 1;
        return b.localeCompare(a, undefined, {
          sensitivity: "base",
        });
      }

      // default: vacía primero, luego A→Z
      if (aEmpty && !bEmpty) return -1;
      if (!aEmpty && bEmpty) return 1;
      return a.localeCompare(b, undefined, {
        sensitivity: "base",
      });
    };

    return Object.entries(byBucket)
      .sort(([a], [b]) => cmpBuckets(a, b))
      .map(([b, items]) => [b, buildTree(items)]);
  }, [rows, sortMode, extraBuckets, bucketSort]);

  // Fallback: si no hay buckets ni contactos, mostramos la sección “Sin subdivisión”
  const bucketsToRender = buckets.length ? buckets : [["", []]];

  /* ---------- Acciones entidad ---------- */
  const handleDeleteEntity = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "Eliminar entidad",
      text: entity?.name
        ? `Se eliminará la entidad "${entity.name}" y todos sus datos asociados.`
        : "Se eliminará la entidad y todos sus datos asociados.",
      showCancelButton: true,
      confirmButtonColor: COLOR_DANGER,
      cancelButtonColor: COLOR_CANCEL,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    await deleteRegionalEntity(entityId);
    await Swal.fire({
      icon: "success",
      title: "Eliminado",
      timer: 900,
      showConfirmButton: false,
    });
    onClose?.();
    onChanged?.();
  };

  /* ---------- Acciones subdivisión ---------- */
  const handleAddSubdivision = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "Nueva subdivisión",
      input: "text",
      inputPlaceholder: "Escribe un nombre",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLOR_PRIMARY,
      cancelButtonColor: COLOR_CANCEL,
      inputValidator: (v) => {
        const name = String(v || "").trim();
        if (!name) return "Escribe un nombre";
        const exists = buckets.some(
          ([b]) => norm(b) === norm(name)
        );
        if (exists)
          return "Ya existe una subdivisión con ese nombre";
        return undefined;
      },
    });
    if (!isConfirmed) return;
    const name = String(value || "").trim();
    if (!name) return;

    setExtraBuckets((prev) => [...prev, name]);
    setBucketFilter("");
    setMenuBucket(null);
    await Swal.fire({
      icon: "success",
      title: "Subdivisión creada",
      timer: 900,
      showConfirmButton: false,
    });
  };

  const handleRenameSubdivision = async (oldName) => {
    if (!oldName && oldName !== "") return;
    if (oldName === "") {
      return Swal.fire(
        "No permitido",
        "La sección “Sin subdivisión” no se puede renombrar.",
        "info"
      );
    }

    const { value, isConfirmed } = await Swal.fire({
      title: "Renombrar subdivisión",
      input: "text",
      inputValue: oldName,
      inputPlaceholder: "Nuevo nombre",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLOR_PRIMARY,
      cancelButtonColor: COLOR_CANCEL,
      inputValidator: (v) => {
        const n = String(v || "").trim();
        if (!n) return "Escribe un nombre";
        if (norm(n) === norm(oldName))
          return "El nombre es el mismo";
        const exists = buckets.some(
          ([b]) => norm(b) === norm(n)
        );
        if (exists)
          return "Ya existe una subdivisión con ese nombre";
        return undefined;
      },
    });
    if (!isConfirmed) return;

    const newName = String(value || "").trim();
    if (!newName || norm(newName) === norm(oldName)) return;

    try {
      Swal.fire({
        title: "Actualizando…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      const affected = rows.filter(
        (w) => norm(w.bucket || "") === norm(oldName)
      );
      await Promise.all(
        affected.map((w) =>
          updateRegionalWorker(w.id, { bucket: newName })
        )
      );

      const data = await listRegionalWorkers({ entityId });
      const listRaw = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setRows(
        listRaw.map((w) => ({
          ...w,
          isAuthority: boolish(
            w.isAuthority ?? w.is_authority
          ),
          parentId: w.parentId ?? w.parent_id ?? null,
          bucket: w.bucket ?? "",
          altPhone: w.altPhone ?? w.alt_phone ?? "",
          assistantName:
            w.assistantName ?? w.assistant_name ?? "",
          assistantPhone:
            w.assistantPhone ?? w.assistant_phone ?? "",
          assistantEmail:
            w.assistantEmail ?? w.assistant_email ?? "",
        }))
      );

      setExtraBuckets((prev) =>
        prev.map((b) => (norm(b) === norm(oldName) ? newName : b))
      );

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Subdivisión renombrada",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.close();
      Swal.fire(
        "No se pudo renombrar",
        e?.message || "",
        "error"
      );
    } finally {
      setMenuBucket(null);
    }
  };

  const handleDeleteSubdivision = async (name) => {
    const label = name || "Sin subdivisión";
    const entityName = entity?.name || "esta entidad";

    // Caso subdivisión virtual sin contactos
    if (isVirtualBucket(name) && !bucketHasWorkers(name)) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: `Eliminar subdivisión`,
        text: entity?.name
          ? `¿Eliminar la subdivisión “${label}” de la entidad “${entityName}”?`
          : `¿Eliminar la subdivisión “${label}”?`,
        showCancelButton: true,
        confirmButtonColor: COLOR_DANGER,
        cancelButtonColor: COLOR_CANCEL,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isConfirmed) return;
      setExtraBuckets((prev) =>
        prev.filter((b) => norm(b) !== norm(name))
      );
      if (bucketFilterValue === name) setBucketFilter("");
      return Swal.fire({
        icon: "success",
        title: "Subdivisión eliminada",
        timer: 900,
        showConfirmButton: false,
      });
    }

    const affected = rows.filter(
      (w) => norm(w.bucket || "") === norm(name)
    );

    const title = name
      ? "Eliminar subdivisión"
      : "Eliminar contactos (sin subdivisión)";

    const html = name
      ? `Se eliminará la subdivisión <b>${label}</b> de la entidad <b>${entityName}</b> y <b>${affected.length}</b> contacto(s) asociados. Esta acción no se puede deshacer.`
      : `Se eliminarán <b>${affected.length}</b> contacto(s) sin subdivisión de la entidad <b>${entityName}</b>. Esta acción no se puede deshacer.`;

    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title,
      html,
      showCancelButton: true,
      confirmButtonColor: COLOR_DANGER,
      cancelButtonColor: COLOR_CANCEL,
      confirmButtonText: name
        ? "Eliminar definitivamente"
        : "Eliminar contactos",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    try {
      Swal.fire({
        title: "Eliminando…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      await Promise.all(
        affected.map((w) => deleteRegionalWorker(w.id))
      );

      const data = await listRegionalWorkers({ entityId });
      const listRaw = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setRows(
        listRaw.map((w) => ({
          ...w,
          isAuthority: boolish(
            w.isAuthority ?? w.is_authority
          ),
          parentId: w.parentId ?? w.parent_id ?? null,
          bucket: w.bucket ?? "",
          altPhone: w.altPhone ?? w.alt_phone ?? "",
          assistantName:
            w.assistantName ?? w.assistant_name ?? "",
          assistantPhone:
            w.assistantPhone ?? w.assistant_phone ?? "",
          assistantEmail:
            w.assistantEmail ?? w.assistant_email ?? "",
        }))
      );

      setExtraBuckets((prev) =>
        prev.filter((b) => norm(b) !== norm(name))
      );
      if (bucketFilterValue === name) setBucketFilter("");

      Swal.close();
      Swal.fire({
        icon: "success",
        title: name
          ? "Subdivisión y contactos eliminados"
          : "Contactos eliminados",
        timer: 1100,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.close();
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    } finally {
      setMenuBucket(null);
    }
  };

  /* ---------- Acciones contacto ---------- */
  const openCreate = (presetBucket = "", parentId = null) =>
    setContactDlg({ presetBucket, parentId });

  const openEdit = (row) =>
    setContactDlg({
      values: row,
      editId: row.id,
      presetBucket: row.bucket || "",
    });

  const doView = async (row) => {
    try {
      const w = await getRegionalWorker(row.id).catch(() => row);
      setViewer(w);
    } catch {
      setViewer(row);
    }
  };

  const saveContact = async (values, ctx) => {
    const payload = clean({
      entityId,
      name: (values.name || "").trim(),
      role: values.role?.trim() || undefined,
      email: values.email?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      institution: values.institution?.trim() || undefined,
      nature: values.nature?.trim() || undefined,
      altPhone: values.altPhone?.trim() || undefined,
      address: values.address?.trim() || undefined,
      assistantName: values.assistantName?.trim() || undefined,
      assistantPhone: values.assistantPhone?.trim() || undefined,
      assistantEmail: values.assistantEmail?.trim() || undefined,
      bucket: values.bucket?.trim() || undefined,
      isAuthority: !!values.isAuthority,
      parentId: values.parentId ? Number(values.parentId) : undefined,
    });

    if (!payload.name) {
      return Swal.fire({
        icon: "warning",
        title: "Falta nombre",
        confirmButtonText: "Entendido",
        confirmButtonColor: COLOR_PRIMARY,
      });
    }

    try {
      if (ctx?.editId) await updateRegionalWorker(ctx.editId, payload);
      else await createRegionalWorker(payload);

      await Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 900,
        showConfirmButton: false,
      });

      setContactDlg(null);

      const data = await listRegionalWorkers({ entityId });
      const listRaw = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setRows(
        listRaw.map((w) => ({
          ...w,
          isAuthority: boolish(
            w.isAuthority ?? w.is_authority
          ),
          parentId: w.parentId ?? w.parent_id ?? null,
          bucket: w.bucket ?? "",
          altPhone: w.altPhone ?? w.alt_phone ?? "",
          assistantName:
            w.assistantName ?? w.assistant_name ?? "",
          assistantPhone:
            w.assistantPhone ?? w.assistant_phone ?? "",
          assistantEmail:
            w.assistantEmail ?? w.assistant_email ?? "",
        }))
      );
    } catch (e) {
      let errorMessage = e?.message || "";
      // Traducir mensajes de error comunes del backend
      if (errorMessage.includes("assistantEmail must be an email")) {
        errorMessage = "El correo del asistente debe tener un formato válido de email";
      } else if (errorMessage.includes("email must be an email")) {
        errorMessage = "El correo debe tener un formato válido de email";
      }
      Swal.fire("No se pudo guardar", errorMessage, "error");
    }
  };

  const removeContact = async (row) => {
    const entityName = entity?.name || "esta entidad";
    const bucketName = row.bucket
      ? row.bucket
      : "Sin subdivisión";

    const ok = await Swal.fire({
      icon: "warning",
      title: "Eliminar contacto",
      text: `¿Eliminar el contacto "${row.name || "sin nombre"}" de la subdivisión "${bucketName}" en la entidad "${entityName}"?`,
      showCancelButton: true,
      confirmButtonColor: COLOR_DANGER,
      cancelButtonColor: COLOR_CANCEL,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteRegionalWorker(row.id);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) =>
        e.target === e.currentTarget && onClose?.()
      }
    >
      {/* Contenedor principal */}
      <div className="w-full max-w-7xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            {entity?.imageUrl ? (
              <img
                src={entity.imageUrl}
                alt={entity?.name}
                className="w-10 h-10 rounded-lg object-contain bg-white ring-1 ring-white/40 p-0.5"
              />
            ) : (
              <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/30">
                <Building2 className="w-5 h-5" />
              </div>
            )}
            <div>
              <h3 className="font-semibold">
                {entity?.name || "Entidad"}
              </h3>
              <div className="text-sm opacity-90">
                {entity?.region || "Sin región"}
                {entity?.website && (
                  <a
                    href={entity.website}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-3 underline decoration-white/50 hover:decoration-white inline-flex items-center gap-1"
                  >
                    <LinkIcon size={14} /> Sitio web
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleAddSubdivision}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs sm:text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] whitespace-nowrap transition-colors"
              title="Añadir subdivisión"
            >
              <Plus size={16} /> Subdivisión
            </button>
            <button
              onClick={() => onEditEntity?.(entity)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs sm:text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] whitespace-nowrap transition-colors"
            >
              <Edit3 size={16} /> Editar entidad
            </button>
            <button
              onClick={handleDeleteEntity}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors"
            >
              <Trash2 size={16} /> Eliminar
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Cuerpo scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Controles */}
          <div className="px-5 pt-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Buscar */}
              <div className="flex-1 min-w-[240px]">
                <Label className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">Buscar</Label>
                <input
                  className="w-full rounded-xl border px-3 py-2.5 shadow-sm focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A] placeholder:text-gray-400"
                  placeholder="Nombre, cargo, correo o teléfono…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              {/* Subdivisión */}
              <div>
                <Label className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">Subdivisión</Label>
                <select
                  className="h-11 rounded-xl border px-3 bg-white text-sm text-[#09274A]"
                  value={bucketFilter}
                  onChange={(e) =>
                    setBucketFilter(e.target.value)
                  }
                >
                  <option value="">(Todas)</option>
                  {bucketsToRender.map(([b]) => {
                    const value =
                      b === "" ? NO_BUCKET_VALUE : b;
                    return (
                      <option key={value} value={value}>
                        {b || "SIN SUBDIVISIÓN"}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Orden contactos */}
              <div>
                <Label className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">Orden contactos</Label>
                <select
                  className="h-11 rounded-xl border px-3 bg-white text-sm text-[#09274A]"
                  value={sortMode}
                  onChange={(e) =>
                    setSortMode(e.target.value)
                  }
                >
                  <option value="default">
                    Autoridad primero
                  </option>
                  <option value="az">A → Z</option>
                  <option value="za">Z → A</option>
                </select>
              </div>

              {/* Orden subdivisiones */}
              <div>
                <Label className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">Orden subdivisiones</Label>
                <select
                  className="h-11 rounded-xl border px-3 bg-white text-sm text-[#09274A]"
                  value={bucketSort}
                  onChange={(e) =>
                    setBucketSort(e.target.value)
                  }
                >
                  <option value="default">Por defecto</option>
                  <option value="az">A → Z</option>
                  <option value="za">Z → A</option>
                </select>
              </div>

              {/* Jefaturas alineado con filtros */}
              <div>
                <Label>Jefaturas</Label>
                <label className="inline-flex items-center gap-2 h-11 rounded-xl border px-3 text-sm text-gray-700 cursor-pointer bg-white">
                  <input
                    type="checkbox"
                    checked={onlyAuthority}
                    onChange={(e) =>
                      setOnlyAuthority(e.target.checked)
                    }
                  />
                  Solo jefaturas
                </label>
              </div>
            </div>
          </div>

          {/* Listado por bucket */}
          <div className="px-5 pb-5 pt-4">
            {loading ? (
              <div className="rounded-2xl border bg-white p-4 text-gray-500">
                Cargando…
              </div>
            ) : (
              <div className="space-y-6">
                {bucketsToRender
                  .filter(
                    ([b]) =>
                      !bucketFilterValue ||
                      b === bucketFilterValue
                  )
                  .map(([b, roots]) => (
                    <section
                      key={b || "(none)"}
                      className="rounded-2xl border-2 border-[#0c3b66]/15 bg-white overflow-visible relative"
                    >
                      {/* Encabezado de subdivisión */}
                      <div className="flex items-center justify-between border-b px-4 py-3 bg-slate-50/40 select-none">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold uppercase text-xs tracking-wide text-gray-700">
                            {bucketLabel(b)}
                          </div>

                          {/* Añadir contacto en la subdivisión */}
                          <button
                            type="button"
                            onClick={() =>
                              openCreate(b || "")
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] px-2.5 py-1.5 text-xs font-semibold text-white shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors"
                            title="Añadir contacto en esta subdivisión"
                          >
                            <Plus size={14} /> Añadir contacto
                          </button>
                        </div>

                        <div
                          className="relative"
                          data-bucket-menu-area
                        >
                          <button
                            onClick={() =>
                              setMenuBucket((v) =>
                                v === b ? null : b
                              )
                            }
                            className="p-2 rounded-lg hover:bg-gray-100"
                            aria-label="Opciones"
                            title="Opciones"
                          >
                            <MoreVertical size={18} />
                          </button>

                          {menuBucket === b && (
                            <div className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg z-[300]">
                              {b !== "" ? (
                                <>
                                  <button
                                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
                                    onClick={() =>
                                      handleRenameSubdivision(b)
                                    }
                                  >
                                    <Edit3 size={16} /> Renombrar
                                  </button>
                                  <button
                                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-red-600 inline-flex items-center gap-2"
                                    onClick={() =>
                                      handleDeleteSubdivision(b)
                                    }
                                  >
                                    <Trash2 size={16} /> Eliminar
                                    subdivisión
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-red-600 inline-flex items-center gap-2"
                                  onClick={() =>
                                    handleDeleteSubdivision("")
                                  }
                                >
                                  <Trash2 size={16} /> Eliminar
                                  contactos (sin subdivisión)
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="p-3 space-y-3">
                        {roots.length === 0 ? (
                          <div className="text-sm text-gray-600 px-2 py-3 flex items-center justify-between">
                            <span>
                              Sin contactos en esta subdivisión.
                            </span>
                          </div>
                        ) : (
                          roots.map((n) => (
                            <ContactNode
                              key={n.id}
                              node={n}
                              onAddChild={(parentId) =>
                                openCreate(b || "", parentId)
                              }
                              onView={doView}
                              onEdit={openEdit}
                              onDelete={removeContact}
                            />
                          ))
                        )}
                      </div>
                    </section>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex justify-end bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Modales */}
      {contactDlg && (
        <ContactDialog
          values={contactDlg.values}
          presetBucket={contactDlg.presetBucket}
          defaultParentId={contactDlg.parentId}
          parentCandidates={rows.filter(
            (r) => r.id !== contactDlg.values?.id
          )}
          onCancel={() => setContactDlg(null)}
          onSave={(vals) =>
            saveContact(vals, { editId: contactDlg.editId })
          }
        />
      )}

      {viewer && (
        <WorkerDetailsModal
          open={!!viewer}
          worker={viewer}
          entityName={entity?.name}
          onClose={() => setViewer(null)}
          onEdit={(w) => {
            setViewer(null);
            openEdit(w);
          }}
          onDelete={(w) => {
            setViewer(null);
            removeContact(w);
          }}
        />
      )}
    </div>
  );
}

/* =========================================================
   COMPONENTES: Nodo
   ========================================================= */
function ContactNode({
  node,
  onAddChild,
  onView,
  onEdit,
  onDelete,
  level = 0,
}) {
  const [open, setOpen] = useState(true);
  const hasChildren = !!(node.children?.length);
  const isBoss =
    node.isAuthority === true || node.is_authority === true;

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onView(node);
    }
  };

  return (
    <div
      className={`group relative rounded-2xl p-[1.5px] transition-all
        ${
          isBoss
            ? "bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500"
            : "bg-gradient-to-r from-cyan-300 via-sky-400 to-blue-500"
        }
        hover:shadow-lg hover:shadow-sky-100/60
      `}
      style={{ marginLeft: level * 16 }}
      role="button"
      tabIndex={0}
      onClick={() => onView(node)}
      onKeyDown={handleKey}
    >
      <div className="relative rounded-2xl bg-white ring-1 ring-black/5">
        <div
          className={`absolute inset-y-3 left-3 w-[3px] rounded-full
            ${
              isBoss
                ? "bg-gradient-to-b from-amber-500 to-amber-300"
                : "bg-gradient-to-b from-sky-500 to-cyan-300"
            }`}
        />

        <div className="p-4 pl-6 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  hasChildren && setOpen((v) => !v);
                }}
                className={`mr-1 grid h-6 w-6 place-items-center rounded-md border text-gray-600 ${
                  hasChildren
                    ? "hover:bg-gray-50"
                    : "opacity-0 pointer-events-none"
                }`}
                title={open ? "Contraer" : "Expandir"}
                aria-label={open ? "Contraer" : "Expandir"}
              >
                {open ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>

              <div className="font-medium truncate">
                {node.name}
              </div>
              {isBoss && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-[2px] text-xs text-amber-700 border border-amber-200">
                  <ShieldAlert size={12} /> Autoridad
                </span>
              )}
            </div>

            <div className="mt-1 text-sm text-gray-700 flex items-center gap-3 flex-wrap">
              {node.role && (
                <span className="inline-flex items-center gap-1 text-gray-700">
                  <Briefcase
                    size={14}
                    className="opacity-70"
                  />{" "}
                  {node.role}
                </span>
              )}
              {node.email && (
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <Mail
                    size={14}
                    className="opacity-70"
                  />{" "}
                  {node.email}
                </span>
              )}
              {node.phone && (
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <Phone
                    size={14}
                    className="opacity-70"
                  />{" "}
                  {node.phone}
                </span>
              )}
              {(node.altPhone ?? node.alt_phone) && (
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <PhoneCall
                    size={14}
                    className="opacity-70"
                  />{" "}
                  {node.altPhone ?? node.alt_phone}
                </span>
              )}
            </div>
          </div>

          {/* Acciones rápidas del nodo */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(node);
              }}
              title="Editar"
              className="p-1.5 rounded-md border hover:bg-gray-50"
            >
              <Edit3 size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(node);
              }}
              title="Eliminar"
              className="p-1.5 rounded-md border hover:bg-gray-50 text-red-600"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {hasChildren && open && (
          <div
            className="mt-2 space-y-2 pb-3 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {node.children.map((c) => (
              <ContactNode
                key={c.id}
                node={c}
                onAddChild={onAddChild}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   DIÁLOGO CONTACTO
   ========================================================= */
function ContactDialog({
  values,
  presetBucket = "",
  defaultParentId = null,
  parentCandidates = [],
  onCancel,
  onSave,
}) {
  const [form, setForm] = useState({
    name: values?.name || "",
    role: values?.role || "",
    email: values?.email || "",
    phone: values?.phone || "",
    altPhone: values?.altPhone || "",
    address: values?.address || "",
    assistantName: values?.assistantName || "",
    assistantPhone: values?.assistantPhone || "",
    assistantEmail: values?.assistantEmail || "",
    bucket: values?.bucket ?? presetBucket ?? "",
    isAuthority:
      (values?.isAuthority ?? values?.is_authority) || false,
    parentId: values?.parentId ?? defaultParentId ?? "",
  });

  const set = (k, v) =>
    setForm((s) => ({
      ...s,
      [k]: v,
    }));

  const parentOpts = useMemo(
    () =>
      parentCandidates
        .filter((c) => c.id !== values?.id)
        .sort((a, b) =>
          String(a.name || "").localeCompare(
            String(b.name || "")
          )
        ),
    [parentCandidates, values?.id]
  );

  const bucketSuggestions = useMemo(() => {
    const s = new Set();
    if (presetBucket) s.add(presetBucket);
    if (values?.bucket) s.add(values.bucket);
    parentCandidates.forEach((c) => {
      const b = (c.bucket || "").trim();
      if (b) s.add(b);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [presetBucket, values?.bucket, parentCandidates]);

  return (
    <div className="fixed inset-0 z-[250] bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="px-5 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white">
          <h3 className="text-lg font-semibold">
            {values?.id ? "Editar contacto" : "Añadir contacto"}
          </h3>
          <p className="text-sm opacity-90">
            Completa los datos. Los campos sin asterisco son
            opcionales.
          </p>
        </div>

        {/* Body */}
        <div className="p-5">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>
                Nombre <span className="text-red-500">*</span>
              </Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>

            <div>
              <Label className="flex items-center gap-1">
                Subdivisión
                <TooltipHelper text="Agrupa contactos por área, región o departamento. Los contactos con la misma subdivisión aparecen juntos en la lista." />
              </Label>
              <AutocompleteInput
                value={form.bucket}
                onChange={(v) => set("bucket", v)}
                options={bucketSuggestions}
                placeholder="Ej: Elqui, Limarí, SEREMI Educación…"
              />
            </div>

            <div>
              <Label>Cargo</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
              />
            </div>

            <div>
              <Label>Correo</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>

            <div>
              <Label>Teléfono</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>

            <div>
              <Label className="flex items-center gap-1">
                Contacto alternativo
                <TooltipHelper text="Teléfono alternativo o secundario del contacto (celular, directo, etc.)." />
              </Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.altPhone}
                onChange={(e) =>
                  set("altPhone", e.target.value)
                }
              />
            </div>

            <div className="md:col-span-2">
              <Label>Dirección</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.address}
                onChange={(e) =>
                  set("address", e.target.value)
                }
              />
            </div>

            <div>
              <Label>Nombre asistente/Secretaría</Label>
              <Label>Nombre asistente/Secretaría</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.assistantName}
                onChange={(e) =>
                  set("assistantName", e.target.value)
                }
              />
            </div>

            <div>
              <Label>Teléfono Asistente</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.assistantPhone}
                onChange={(e) =>
                  set("assistantPhone", e.target.value)
                }
              />
            </div>

            <div>
              <Label>Correo Asistente</Label>
              <input
                className="w-full rounded-lg border p-2"
                value={form.assistantEmail}
                onChange={(e) =>
                  set("assistantEmail", e.target.value)
                }
              />
            </div>

            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isAuthority}
                  onChange={(e) =>
                    set("isAuthority", e.target.checked)
                  }
                />
                <span className="flex items-center gap-1">
                  Es jefatura / autoridad
                  <TooltipHelper text="Marca esta opción si el contacto es una autoridad, jefe o tiene un cargo de liderazgo dentro de la entidad." />
                </span>
              </label>

              <div>
                <Label>Depende de (opcional)</Label>
                <select
                  className="w-full rounded-lg border p-2"
                  value={form.parentId ?? ""}
                  onChange={(e) => set("parentId", e.target.value || "")}
                >
                  <option value="">(Sin superior)</option>
                  {parentOpts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-5 flex justify-end gap-2">
            <button
              className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
              onClick={onCancel}
            >
              Cancelar
            </button>
            <button
              className="h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold transition-colors"
              onClick={() => onSave(form)}
            >
              {values?.id ? "Guardar cambios" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
